import HandleClient from "@koralabs/adahandle-sdk";
import { useI18N } from "@sundae/react-hooks";
import { Button, CheckCircledIcon, Dialog, HandleLogo, Text, Tooltip } from "@sundae/ui-toolkit";
import { IMappedAsset, useWalletBalance } from "@sundae/wallet-react";
import cx from "classnames";
import { FC, useCallback, useMemo } from "react";

import { testIds } from "../../../constants/testIds.constants";
import { Actions, PersistentActions, useAppContext, useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import { truncateString } from "../../../utils/string-format";
import LoadingComponent from "../../LoadingComponent";
import NoDataIndicator from "../../NoDataIndicator";

const ChooseHandleDialog: FC = () => {
  const { t } = useI18N("wallet");
  const { balance, query: balanceQuery } = useWalletBalance();
  const {
    state: {
      dialogs: { pickHandle },
    },
    dispatch,
  } = useAppContext();
  const {
    state: { handle },
    dispatch: persistentDispatch,
  } = useProfileAppContext();

  const handles = useMemo(() => {
    if (!balance) {
      return [];
    }

    return balance.getHandles() as [string, IMappedAsset<IAssetMetaData>][];
  }, [balance]);

  const setPreferredHandle = useCallback(
    (key: string) => {
      PersistentActions.setPreferredHandle(key, persistentDispatch);
      Actions.setDialogState({ dialog: "pickHandle", isOpen: false }, dispatch);
    },
    [dispatch, persistentDispatch]
  );

  return (
    <Dialog
      open={pickHandle.isOpen}
      closeDialog={() => Actions.setDialogState({ dialog: "pickHandle", isOpen: false }, dispatch)}
      description={t("dialogs.handle_desc")}
      title={t("dialogs.handle_title")}
      testId={testIds.chooseHandleDialog.content}
    >
      <div>
        {balanceQuery.isLoading && <LoadingComponent />}

        {balance && handles.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {handles
              .sort(([keyA, { metadata: metadataA }], [, { metadata: metadataB }]) => {
                if (handle === keyA || !metadataA.ticker || !metadataB.ticker) {
                  return -1;
                }

                const nameA = HandleClient.getNormalizedName({
                  value: metadataA.assetId.split(".")[1],
                }).value;

                const nameB = HandleClient.getNormalizedName({
                  value: metadataB.assetId.split(".")[1],
                }).value;

                return nameA.localeCompare(nameB);
              })
              .map(([key]) => {
                const hexName = key.split(".")[1] || "";
                if (!!hexName) {
                  const resolvedName = HandleClient.getNormalizedName({
                    value: hexName,
                  }).value;

                  return (
                    <Button
                      key={key}
                      onClick={() => {
                        setPreferredHandle(hexName);
                      }}
                      disabled={key === handle}
                      variant="neutral"
                      size="sm"
                      className={cx("flex w-full items-center justify-center", {
                        "!border-2 !border-success !opacity-100": key === handle,
                      })}
                    >
                      <Text className="flex items-center justify-center gap-2" tag="span" size="sm">
                        <Tooltip
                          tooltipLabel={
                            <div className="flex items-center">
                              <HandleLogo className="h-4 w-4" />
                              {resolvedName}
                            </div>
                          }
                          trigger={
                            <div className="flex items-center">
                              <HandleLogo className="h-4 w-4" />
                              {truncateString(resolvedName)}
                            </div>
                          }
                        />
                        {key === handle && <CheckCircledIcon className="text-success" />}
                      </Text>
                    </Button>
                  );
                }
              })}
          </div>
        ) : (
          <NoDataIndicator context="handles" size="sm" />
        )}
      </div>
    </Dialog>
  );
};

export default ChooseHandleDialog;
